import { Box, Button, Container, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { MaterialReactTable } from "material-react-table";
// import {csv} from "papaparse";
import { getColumns } from "./columns";
import { PoliciesToSendContext } from "./PoliciesToSendContext";
import { useRef, useContext, useEffect, useMemo, useState } from "react";
import PoliciesToSendActions from "./PoliciesToSendActions";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { UserContext } from "../../../contexts/UserContext";
import { InternalUser } from "../../../types/app";
import { NotesColumn } from "../../NotesColumn";
import { exportTableToCsv } from "../../../taskUtils";
import Iconify from "../../../../minimals/components/iconify";
import { Policy } from "../policyIssues/types";
import NotesModal from "../../../checks/checkNotes/NotesModal";
import UploadModal from "../../../shared/UploadModal";
import ChangeAssignedToModal from "./ChangeAssignedToModal";
import { convertToFileDownload } from "../../../utils/convertToFileDownload";
import { DashboardReportType } from "../../../types/reports";

export default function PoliciesToSendDashboard() {
  const {
    policies,
    loading,
    setDateRange,
    dateRange,
    assignedToMe,
    setAssignedToMe,
    fetchPolicies,
    setPolicies,
    tableRef
  } = useContext(PoliciesToSendContext);
  const { user } = useContext(UserContext);
  const [managerDepartments, setManagerDepartments] = useState<string[]>([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [selectedUploadPolicy, setSelectedUploadPolicy] = useState<Policy | null>(null);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openAssignedTo, setOpenAssignedTo] = useState(false);
  const [assignableInternalUserIds, setAssignableInternalUserIds] = useState<number[]>([]);
  const documentInfo = {
    description: `${Number(selectedUploadPolicy?.type) === 2 ? "LP " : "OP "}${
      selectedUploadPolicy?.position ? selectedUploadPolicy?.position : ""
    } Transmittal Confirmation`,
    base64File: "",
    fileName: "",
    subcategoryId: "PCYCOR",
    mTANumber: selectedUploadPolicy?.orderNumber,
    applicationInitiator: "TitleQ.PoliciesToSend"
  };
  const getAndSetManagerDepartmentsByEmail = async (emailAddress: string) => {
    const { data } = await axios.get<string[]>(
      `/api/outstandingchecks/getManagerDepartmentsByEmail?emailAddress=${emailAddress}`
    );
    setManagerDepartments(data);
  };

  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };
  const getAndSetAssignableInternalUserIds = async () => {
    const id = assignedToId && !isUnassignedOnly ? Number(assignedToId) : user?.id;
    if (!id) {
      setAssignableInternalUserIds([]);
      return;
    }

    const { data } = await axios.get<number[]>(
      `/api/clientphonebook/internalusers/getInternalUserIdsIncludingPairedUsers?internalUserIds=${id}`
    );
    setAssignableInternalUserIds(data);
  };

  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };

  const onSaveUpload = async (documentIds: number[]) => {
    const documentId = documentIds[0];
    if (!documentId) return;
    await axios.post("/api/outstandingChecks/AttachPolicyTransmittalDocument", {
      documentId,
      policyId: selectedUploadPolicy?.id
    });
    fetchPolicies();
  };
  const onSaveAssignedTo = async (selectedAssignedTo: any) => {
    await axios.post("/api/outstandingChecks/UpdatePolicy", {
      ...selectedUploadPolicy,
      assignedToSendName: selectedAssignedTo.fullName,
      assignedToSendEmail: selectedAssignedTo.email
    });
    fetchPolicies();
    setOpenAssignedTo(false);
  };
  const filteredPolicies = useMemo(() => {
    const assignedToIds = assignableInternalUserIds.length
      ? assignableInternalUserIds
      : assignedToId && !isUnassignedOnly
        ? [Number(assignedToId)]
        : user?.id
          ? [user.id]
          : [];
    if (isUnassignedOnly) {
      return policies.filter((p: Policy) => !p.assignedToUser);
    }
    if (!assignedToMe) return policies;

    if (assignedToId) {
      return policies.filter((p: Policy) => !!p.assignedToSendUser?.id && assignedToIds.includes(p.assignedToSendUser.id));
    }
    return policies.filter((p) => !!p.assignedToSendUser?.id && assignedToIds.includes(p.assignedToSendUser.id));
  }, [assignedToMe, policies, assignedToId, assignableInternalUserIds, user?.id, managerDepartments]);

  useEffect(() => {
    getAndSetAssignedToUser();
  }, [assignedToId]);

  useEffect(() => {
    getAndSetAssignableInternalUserIds();
  }, [assignedToId, user?.id]);

  useEffect(() => {
    if (user?.email) {
      getAndSetManagerDepartmentsByEmail(user.email);
    }
  }, [user]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };
  const handleOpenModal = (selectedPolicy: Policy) => {
    setSelectedUploadPolicy(selectedPolicy);
    setOpenUpload(true);
  };
  const handleCloseModal = () => {
    setOpenUpload(false);
    setSelectedUploadPolicy(null);
  };
  const handleOpenAssignedTo = (selectedPolicy: Policy) => {
    setSelectedUploadPolicy(selectedPolicy);
    setOpenAssignedTo(true);
  };

  const columns = getColumns(handleOpenModal, setSelectedUploadPolicy, handleOpenAssignedTo);
  const columnsWithNotes = NotesColumn<Policy>(columns, setSelectedPolicy);

  return (
    <>
      <Helmet>
        <title> Policies to Send | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" marginBottom={2}>
          Policies to Send
          {isUnassignedOnly && " - Unassigned"}
          {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
        </Typography>
        <MaterialReactTable
          enableStickyHeader
          columns={columnsWithNotes}
          data={filteredPolicies}
          enableGlobalFilter
          enableFacetedValues
          enableColumnOrdering={enableOrdering}
          autoResetPageIndex={false}
          globalFilterFn="includesString"
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 },
            columnFilters: [{ id: "status", value: [2] }],
            columnVisibility: {
              dept: false
            }
          }}
          muiTableBodyProps={{
            sx: {
              "& .MuiTableRow-root:hover .MuiTableCell-root": {
                backgroundColor: "inherit !important"
              }
            }
          }}
          muiTableBodyRowProps={({ row }) => {
            return {
              style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {}
            };
          }}
          state={{ showSkeletons: loading }}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <Box sx={{ flexGrow: 1, position: "relative" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PoliciesToSendActions
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    assignedToMe={assignedToMe}
                    setAssignedToMe={setAssignedToMe}
                    table={table}
                    hideAssignedToMeOptions={!!assignedToId}
                    onRowsDeselected={onRowsDeselected}
                  />
                  <Box sx={{ position: "absolute", right: 0 }}>
                    <Tooltip title="Column ordering">
                      <IconButton onClick={toggleColumnOrdering}>
                        <Iconify icon="mdi:reorder-vertical" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to csv">
                      <IconButton onClick={() => exportTableToCsv(table, "export.csv")}>
                        <Iconify icon="eva:download-fill" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
              </Box>
            );
          }}
        />
      </Container>
      {!!selectedPolicy && (
        <NotesModal
          isOpen
          nameBeforeUpdate={selectedPolicy?.assignedToUser?.fullName || ""}
          emailBeforeUpdate={selectedPolicy?.assignedToUser?.email || ""}
          entity={selectedPolicy}
          setEntity={setSelectedPolicy}
          setEntityTable={setPolicies}
          refetchData={() => fetchPolicies()}
          reportType={DashboardReportType.PoliciesToSend}
        />
      )}
      {openUpload && (
        <UploadModal
          orderNumber={selectedUploadPolicy?.orderNumber || ""}
          updateItems={fetchPolicies}
          open={openUpload}
          handleClose={handleCloseModal}
          onSave={onSaveUpload}
          documentInfo={documentInfo}
          applicationInitiator="TitleQ.PoliciesToSend"
        />
      )}
      {openAssignedTo && (
        <ChangeAssignedToModal isOpen={openAssignedTo} onSave={onSaveAssignedTo} setIsOpen={setOpenAssignedTo} />
      )}
    </>
  );
}
